<template>
    <div class="bg-base-200 h-screen-header rounded-xl md:p-2 overflow-y-auto overflow-x-hidden md:overflow-x-auto fade-in">
        <!-- 主内容区 -->
        <main>
            <div class="flex flex-col md:flex-row">
                <div class="flex-1">
                    <!-- 顶部导航栏 -->
                    <header class="p-4 flex items-center justify-between">
                        <div class="flex items-center space-x-4">
                            <a href="#" class="text-base-content/60 hover:text-base-content">{{ $tt('发现音乐') }}</a>
                        </div>
                    </header>
                    <!-- 最热歌曲 -->
                    <div class="relative slide-in-left">
                        <div class="h-32 md:h-48 2xl:h-96 p-8 rounded-xl hover-glow transition-smooth"
                            :style="{ backgroundImage: `url(${callCoverArtUrl(hotTrack.cover)})`, backgroundSize: 'cover', backgroundPosition: 'center' }">
                            <div class="flex items-center space-x-2 mb-4">
                                <i class="fas fa-fire text-primary"></i>
                                <span class="text-sm text-base-100">{{ hotTrack.artist_name }}</span>
                            </div>
                            <h1 class="text-4xl md:text-6xl font-bold mb-4 text-base-100 truncate max-w-96">{{ hotTrack.name }}
                            </h1>
                            <div class="flex items-center space-x-2 text-base-100">
                                <span> This song has been listened to {{ formatNumber(hotTrack.play_count) }} times.
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- 热度艺术家 -->
                    <section class="py-8 md:px-2 slide-in-right transition-fast">
                        <h2 class="text-2xl font-bold mb-6 flex items-center text-base-content bounce-in">
                            <i class="fas fa-chart-line text-primary mr-3"></i>
                            {{ $tt('热门·艺术家') }}
                        </h2>
                        <div class="carousel carousel-center bg-base-50 rounded-box space-x-4 p-1">
                            <div v-for="(artist, index) in hotArtists" :key="artist.name"
                                class="carousel-item bg-base-100 rounded-lg overflow-hidden group cursor-pointer shadow-lg hover:shadow-xl transition-smooth hover-scale click-feedback flex flex-col flex-shrink-0 w-1/3">
                                <div class="relative" @click="redirectToArtist(artist.id)">
                                    <img :src="callCoverArtUrl(artist.cover)"
                                        class="w-full h-40 object-cover opacity-80 group-hover:opacity-100 transition-opacity">
                                    <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-b">
                                    </div>
                                    <div
                                        class="absolute top-4 right-4 bg-base-100 bg-opacity-70 rounded-full px-2 py-1 text-xs">
                                        <i class="fas fa-fire text-green-500 mr-1"></i>
                                        <span>TOP {{ index + 1 }}</span>
                                    </div>
                                </div>
                                <div class="p-4">
                                    <div class="flex items-center justify-between mb-2">
                                        <h3 class="font-bold text-xl text-base-content">{{ artist.name }}</h3>
                                        <span class="text-primary flex items-center text-sm">
                                            <i class="fas fa-user-friends mr-1"></i>
                                            {{ formatNumber(artist.play_count) }}
                                        </span>
                                    </div>
                                    <p class="text-base-content/60 mb-2">{{ artist.genres }}</p>
                                    <div
                                        class="flex items-center text-base-content/60 text-sm max-h-16 min-h-10 overflow-hidden">
                                        <span>Latest: {{ artist.latest_album.name }} {{ artist.latest_album.year ?
                                            `(${artist.latest_album.year})` : '' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <!-- 热门歌曲列表 -->
                    <section class="md:py-8 md:px-8">
                        <h2 class="text-2xl font-bold mb-6 flex items-center text-base-content">
                            <i class="fas fa-fire text-primary mr-3"></i>
                            {{ $tt('热门·推荐') }}
                        </h2>
                        <table class="w-full">
                            <thead>
                                <tr class="text-base-content/60 text-left border-b border-base-300">
                                    <th class="pb-2 w-12">#</th>
                                    <th class="pb-2">{{ $tt('歌曲') }}</th>
                                    <th class="pb-2">{{ $tt('专辑') }}</th>
                                    <th class="pb-2"></th>
                                    <th class="pb-2 text-right">{{ $tt('时长') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(song, index) in randomSongs" :key="song.id"
                                    class="hover:bg-base-200 group border-b border-base-300 cursor-pointer"
                                    @click="play(index)">
                                    <td class="py-2">{{ index + 1 }}</td>
                                    <td class="py-2 flex items-center space-x-3">
                                        <img :src="song.image" class="w-10 h-10 rounded object-cover" v-if="song.image">
                                        <img v-else src="/static/dist/img/album_null.jpg"
                                            class="w-10 h-10 rounded object-cover">
                                        <div>
                                            <div class="font-medium">{{ song.title }}</div>
                                            <div class="text-sm text-base-content/60">{{ song.artist }}</div>
                                        </div>
                                    </td>
                                    <td class="py-2">{{ song.album }}</td>
                                    <td class="py-2">
                                        <button
                                            class="invisible group-hover:visible text-base-content/60 hover:text-primary"
                                            @click="$store.commit('toggleFavorite', song)">
                                            <i :class="['far', song.starred ? 'fas text-primary' : 'far']"
                                                class="fa-heart"></i>
                                        </button>
                                    </td>
                                    <td class="py-2 text-right">{{ formatDuration(song.duration) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <!-- 推荐专辑展示 -->
                    <section class="py-8 md:px-8">
                        <h2 class="text-2xl font-bold mb-6 flex items-center justify-between text-base-content">
                            <div class="flex items-center">
                                <i class="fas fa-compact-disc text-primary mr-3"></i>
                                <span>{{ $tt('推荐专辑') }}</span>
                            </div>
                            <button class="text-sm text-primary hover:text-primary-focus flex items-center"
                                @click="redirectToAlbums()">
                                <span>{{ $tt('查看全部') }}</span>
                            </button>
                        </h2>

                        <div class="grid grid-cols-4 gap-6">
                            <div v-for="album in recommendAlbums" :key="album.id" class="group">
                                <div class="relative mb-3 overflow-hidden rounded-lg">
                                    <img :src="callCoverArtUrl(album.cover)"
                                        class="w-full aspect-square object-cover transform group-hover:scale-110 transition-transform duration-300">
                                    <div
                                        class="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <button
                                            class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center"
                                            @click="playAlbumNow(album.id)">
                                            <i class="fas fa-play text-base-100"></i>
                                        </button>
                                    </div>
                                    <div
                                        class="absolute top-3 right-3 bg-base-100 bg-opacity-70 rounded-full px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                        <i class="fas fa-headphones text-base-100 mr-1"></i>
                                        <span>{{ formatNumber(album.play_count) }}</span>
                                    </div>
                                </div>
                                <h3 class="font-bold">{{ album.name }}</h3>
                                <p class="text-sm text-gray-400">{{ album.artist_name }} • {{ album.year }}</p>
                            </div>
                        </div>
                    </section>

                    <!-- 最新音乐发布 -->
                    <section class="py-8 md:px-8 bg-base-100 rounded-xl shadow-lg !hidden md:!block">
                        <h2 class="text-2xl font-bold mb-6 flex items-center text-base-content">
                            <i class="fas fa-calendar-alt text-primary mr-3"></i>
                            <span>{{ $tt('最新发布') }}</span>
                        </h2>

                        <div v-for="album in newAlbums" :key="album.id"
                            class="flex flex-col md:flex-row items-center space-x-6 mb-6">
                            <div class="w-56 h-56 flex-shrink-0 cursor-pointer" @click="redirectToAlbum(album.id)">
                                <img :src="callCoverArtUrl(album.cover)"
                                    class="w-full h-full object-cover rounded-lg shadow-lg">
                            </div>

                            <div class="flex-1">
                                <div class="flex items-center mb-4">
                                    <span class="badge badge-primary mr-3">{{ album.type }}</span>
                                    <span class="text-base-content/60 text-sm">{{ $tt('发布于 {date}', { date: album.created_at }) }}</span>
                                </div>

                                <h3 class="text-3xl font-bold mb-3 text-base-content">{{ album.name }}</h3>
                                <p class="text-lg text-base-content/80 mb-4">{{ album.comment }}</p>

                                <div class="flex items-center space-x-2 mb-6">
                                    <div class="flex items-center space-x-1">
                                        <i v-for="n in 5" :key="n"
                                            :class="['fas', n <= Math.floor(album.rating) ? 'fa-star text-warning' : 'fa-star-half-alt text-warning']"></i>
                                    </div>
                                    <!-- <span class="text-sm text-base-content/60">{{ album.rating }} ({{
                                        formatNumber(album.rating_count) }} 评价)</span> -->
                                </div>

                                <div class="text-sm text-base-content/60 mb-6">
                                    <p>{{ album.comment }}</p>
                                </div>

                                <div class="flex space-x-4">
                                    <button class="btn btn-primary btn-sm" @click="playAlbumNow(album.id)">
                                        <span>{{ $tt('立即播放') }}</span>
                                    </button>
                                    <button :class="['btn btn-sm', album.favourite ? 'btn-primary' : 'btn-outline']"
                                        @click="addFavouriteAlbum(album)">
                                        <span>{{ $tt('添加到喜爱') }}</span>
                                    </button>
                                </div>  
                            </div>
                        </div>
                    </section>
                </div>
                <!-- 右侧推荐区域 -->
                <div class="md:w-96 md:py-6 md:px-6">
                    <div class="sticky top-0">
                        <div class="mb-6 bg-base-100 p-4 rounded-xl shadow-lg">
                            <h2 class="text-sm font-bold text-base-content/60 mb-2">{{ $tt('精选专辑') }}</h2>
                            <div class="relative mb-4">
                                <img :src="callCoverArtUrl(album.cover)"
                                    class="w-full h-64 object-cover rounded-lg mb-4">
                                <div
                                    class="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                                    <h3 class="text-2xl font-bold mb-2 text-base-100">{{ album.name }}</h3>
                                    <p class="text-gray-400">{{ album.comment }}</p>
                                </div>
                                <div class="absolute top-3 right-3 flex space-x-2">
                                    <button
                                        class="w-8 h-8 bg-black bg-opacity-60 rounded-full flex items-center justify-center hover:bg-opacity-80"
                                        @click="redirectToAlbum(album.id)">
                                    </button>
                                    <button
                                        class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center hover:bg-green-500"
                                        @click="playAlbumNow(album.id)">
                                    </button>
                                </div>
                            </div>
                            <div class="flex items-center justify-between mb-2">
                                <div>
                                    <div class="text-xs text-gray-400 mb-1">{{ $tt('专辑 • {year}', { year: album.year }) }}</div>
                                    <div class="flex items-center">
                                        <div class="flex -space-x-1 mr-2">
                                            <img v-for="artist in album.artists" :key="artist.id"
                                                :src="callCoverArtUrl(artist.cover)"
                                                class="w-5 h-5 rounded-full border border-[#1e2329]">
                                        </div>
                                        <span class="text-xs text-gray-400">{{ formatNumber(album.play_count) }}
                                            {{ $tt('收听') }}</span>
                                    </div>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <button class="text-gray-400 hover:text-white">
                                    </button>
                                    <button class="text-gray-400 hover:text-white">
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- 相似艺术家板块 -->
                        <div class="mb-6 bg-base-100 p-4 rounded-xl shadow-lg">
                            <div class="flex items-center justify-between mb-4">
                                <h2 class="text-sm font-bold text-base-content">{{ $tt('相似艺术家') }}</h2>
                                <button class="text-xs text-primary hover:text-primary-focus"
                                    @click="redirectToArtists()">{{ $tt('查看全部') }}</button>
                            </div>
                            <div class="flex space-x-4 mb-4">
                                <div v-for="artist in similarArtists.slice(0, 3)" :key="artist.id"
                                    class="flex-1 text-center">
                                    <div class="relative inline-block cursor-pointer"
                                        @click="redirectToArtist(artist.id)">
                                        <img :src="callCoverArtUrl(artist.cover)"
                                            class="w-14 h-14 rounded-full object-cover mx-auto border-2 border-green-500">
                                    </div>
                                    <p class="text-xs mt-2 font-medium">{{ artist.name }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- 最近播放 -->
                        <div class="space-y-4 bg-base-100 p-4 rounded-xl shadow-lg">
                            <div class="flex items-center justify-between mb-2">
                                <h2 class="text-sm font-bold text-base-content">{{ $tt('最近播放') }}</h2>
                                <button class="text-xs text-primary hover:text-primary-focus"
                                    @click="redirectToRecord()">{{ $tt('查看历史') }}</button>
                            </div>
                            <div v-for="(track, index) in recentPlays" :key="track.id"
                                class="flex items-center space-x-4 hover:bg-base-200 p-2 rounded-lg transition-colors cursor-pointer"
                                @click="playRecentTrack(index)">
                                <div class="relative">
                                    <img :src="callCoverArtUrl(track.coverArt)" class="w-12 h-12 rounded object-cover">
                                    <button
                                        class="absolute inset-0 bg-black bg-opacity-40 rounded flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                        <i class="fas fa-play text-white"></i>
                                    </button>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <h4 class="font-medium truncate">{{ track.title }}</h4>
                                    <p class="text-sm text-gray-400 truncate">{{ track.artist }}</p>
                                </div>
                                <div class="flex items-center space-x-2 flex-shrink-0">
                                    <span class="text-gray-400 text-sm">{{ formatDuration(track.duration) }}</span>
                                </div>
                            </div>

                            <button class="btn btn-outline btn-block btn-sm mt-2" @click="redirectToHistory()">
                                {{ $tt('查看更多') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>
<script>
import TrackList from '@/views/library/track/TrackList'
import { getCoverArtUrl, normalizeTrack } from '@/api/apiUrl/subsonic/serializers'

export default {
    components: {
        TrackList
    },
    data() {
        return {
            discover: [],
            randomSongs: [],
            hotArtists: [],
            similarArtists: [],
            album: {},
            recommendAlbums: [],
            recentPlays: [],
            newAlbums: [],
            hotTrack: {}
        }
    },
    computed: {
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        tracks() {
            return this.$store.state.common.queue
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        }
    },
    created() {
        this.fetchRandomSongs()
        this.fetchHotArtists()
    },
    methods: {
        callCoverArtUrl(coverArt) {
            const url = getCoverArtUrl({ coverArt })
            console.log(url)
            if (url) {
                return url
            } else {
                return '/static/dist/img/album_null.jpg'
            }
        },
        dragstart(id, event) {
            event.dataTransfer.setData('application/x-track-id', id)
        },
        remove(idx) {
            return this.$store.commit('removeFromQueue', idx)
        },
        clear() {
            return this.$store.commit('clearQueue')
        },
        shuffle() {
            return this.$store.commit('shuffleQueue')
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        fetchDiscover() {
            return this.$api.subsonic.getDiscover().then((res) => {
                this.discover = res.discover
            })
        },
        async fetchRandomSongs() {
            try {
                const response = await this.$api.subsonic.getRandomSongs(8)
                this.randomSongs = response || []
                console.log(this.randomSongs)
            } catch (error) {
                console.error('获取随机歌曲失败:', error)
                this.randomSongs = []
            }
        },
        formatNumber(num) {
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + 'M'
            } else if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'K'
            }
            return num
        },
        async fetchHotArtists() {
            try {
                const response = await this.$api.Task.getHotArtists()
                let hotArtists = response.data.hot_artists || []

                // 如果 hotArtists 少于 6 条，用最后一条数据重复填充到 6 条
                if (hotArtists.length > 0 && hotArtists.length < 6) {
                    const lastArtist = hotArtists[hotArtists.length - 1]
                    while (hotArtists.length < 6) {
                        hotArtists.push({ ...lastArtist })
                    }
                }

                this.hotArtists = hotArtists
                this.similarArtists = response.data.similar_artists || []
                this.album = response.data.album || {}
                this.recommendAlbums = response.data.recommend_albums || []
                this.recentPlays = response.data.recent_plays || []
                this.newAlbums = response.data.new_albums || []
                this.hotTrack = response.data.hot_track || {}
            } catch (error) {
                console.error('获取热门艺术家失败:', error)
                this.hotArtists = []
                this.similarArtists = []
                this.album = {}
                this.recommendAlbums = []
                this.recentPlays = []
                this.newAlbums = []
                this.hotTrack = {}
            }
        },
        redirectToArtist(id) {
            this.$router.push({ name: 'artist', params: { id: id } })
        },
        redirectToArtists() {
            this.$router.push({path: '/artists/most-albums/'})
        },
        redirectToAlbum(id) {
            this.$router.push({ name: 'album', params: { id: id } })
        },
        async playAlbumNow(id) {
            const album = await this.$api.subsonic.getAlbumDetails(id)
            return this.$store.dispatch('playTrackList', {
                tracks: album.tracks
            })
        },
        play(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.randomSongs
            })
        },
        redirectToAlbums() {
            this.$router.push({path: '/albums/recently-added/'})
        },
        playRecentTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.recentPlays.map(normalizeTrack)
            })
        },
        redirectToHistory() {
            this.$router.push({ path: '/tracks/recently-played' })
        },
        redirectToRecord() {
            this.$router.push({ name: 'record' })
        },
        addFavouriteAlbum(item) {
            if (item.favourite) {
                this.$api.subsonic.removeFavourite(item.id, 'album').then((res) => {
                    item.favourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(item.id, 'album').then((res) => {
                    item.favourite = true
                })
            }
        }
    }
}
</script>
<style scoped>
.body {
    display: flex;
    justify-content: start;
    align-items: center;
}

.shell {
    position: relative;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    /* 设置3d变化 */
    transform-style: preserve-3d;
    /* 添加透视效果 */
    perspective: 900px;
}

.shell .box {
    position: relative;
    width: 200px;
    height: 270px;
    transition: 0.3s;
    overflow: hidden;
    margin: 15px;
    transform: rotateY(0deg);
    transition-delay: .1s;
    border-radius: 5px;
    border: #fff 5px solid;
}

/* 当鼠标所有卡片的父元素点shell上时，
所有卡片像Y轴翻转20度 */
.shell:hover .box {
    transform: rotateY(20deg);
}

/* 当鼠标悬浮在当前卡片上时，当前卡片的翻转角度为0，
并放大一点二五倍，加个阴影 */
.shell .box:hover {
    transform: rotateY(0deg) scale(1.25);
    box-shadow: 0 25px 40px rgba(0, 0, 0, 0.7);
    z-index: 1;
}

/* 当鼠标悬浮在卡片上时，
当前卡片后面的所有卡片全部都反向翻转负20度 */
.shell .box:hover~.box {
    transform: rotateY(-20deg);
}

.shell .box .images img {
    width: 100%;
}

.shell .box .content {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: 999;
    padding: 15px;
}

.shell .box .content h2 {
    color: rgb(210, 140, 140);
    transition: 0.3s;
    font-size: 20px;
    transform: translateY(-100px);
}

.shell .box:hover .content h2 {
    transform: translateY(-15px);
}

.shell .box .content p {
    color: rgb(0, 0, 0);
    transition: 0.3s;
    font-size: 14px;
    transform: translateY(600px);
    background-color: rgba(255, 255, 255, 0.7);
}

.shell .box:hover .content p {
    transform: translateY(185px);
}
</style>
